// articles/MatchPlayGame.js
// Game guide (Oct 6, 2026): Match Play in TTO, start to finish, for a weekly round first.
// Written from the live code: the Match Play card in Games Setup (App.js
// MatchPlayGamePanel — Matches, Strokes), the engine (gameEngines/matchPlay.js), the
// Results tab (MatchPlayMatches) and the settlement statement. Button names are quoted
// exactly as they appear, so if a label changes in App.js, change it here too.
//
// Linked from inside the app: 📖 Guides → Game guides. Public.

import React from 'react';
import { colors, getGuideBySlug } from '../guidesData';
import { H2, H3, P, UL, LI, GuideCTA, FAQ } from '../articleUI';
import { Step, Btn, Heads, StepTracker, PRINT_CSS } from './eventGuideParts';

const guide = getGuideBySlug('how-to-set-up-a-match-play-golf-game');

const FAQS = [
  {
    q: 'What does “3 & 2” mean?',
    a: 'The match ended with the winner 3 holes up and only 2 left to play, so the other side could no longer catch up. TTO stops counting that match there; holes played after it don’t change it.',
  },
  {
    q: 'What is “dormie”?',
    a: 'A side is up by exactly the number of holes left, say 2 up with 2 to play. The other side has to win every remaining hole just to halve the match.',
  },
  {
    q: 'How are the strokes worked out?',
    a: 'By the USGA’s match play allowances unless you choose otherwise. In singles the lower handicap plays off scratch and the other player gets the difference. In four-ball each player first goes to 90% of his course handicap, then everyone plays off the lowest of the four. Strokes fall on the hardest holes first.',
  },
  {
    q: 'How is Match Play different from a Nassau?',
    a: 'A Nassau is three match-play bets (front, back and overall) with presses. Match Play is one bet over the whole round and no presses: the simplest match there is.',
  },
  {
    q: 'Can we play match play on nine holes?',
    a: 'Yes. On a round set up as a nine, each match is over those nine holes, and the strokes fall on the hardest of those nine.',
  },
  {
    q: 'We have a threesome. Can we play four-ball?',
    a: 'Four-ball needs four players in the group. In a threesome choose singles: each player has a match with each of the other two.',
  },
  {
    q: 'Can I run a match play bracket or a Ryder Cup?',
    a: 'Not yet. Match Play in TTO is the matches inside one round. Brackets and team cups that run over several rounds are on the list.',
  },
];

export default function MatchPlayGame() {
  return (
    <article>
      <h1
        style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontSize: 38,
          fontWeight: 400,
          color: colors.gray900,
          margin: '0 0 8px',
          lineHeight: 1.15,
        }}
      >
        How to Set Up Match Play
      </h1>
      <style>{PRINT_CSS}</style>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '0 0 28px' }}
      >
        <p style={{ fontSize: 15, color: colors.gray400, margin: 0 }}>
          {guide.readMinutes} min read &middot; Game guide
        </p>
        <button
          type="button"
          className="guide-no-print"
          onClick={() => window.print()}
          style={{
            background: 'white',
            color: colors.green800,
            border: `1px solid ${colors.gray300}`,
            borderRadius: 8,
            padding: '8px 14px',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          🖨️ Print this guide
        </button>
      </div>

      <P>
        Match play is golf <strong>hole by hole</strong>. The lower score wins the hole, or it is
        halved, and the side that wins <strong>more holes</strong> wins the match. A match is over
        once a side is up by more holes than are left: 3 up with 2 to play is won{' '}
        <strong>&ldquo;3 &amp; 2&rdquo;</strong>. All square at the end is halved.
      </P>
      <P>
        In TTO each match is <strong>one bet over the round</strong>, 18 holes or a nine. Play{' '}
        <strong>singles</strong> (every player against every other player in his group) or{' '}
        <strong>2 vs 2 four-ball</strong> (the side&rsquo;s better score on each hole counts).
      </P>

      <StepTracker
        storageKey="tto_guide_match_play_steps"
        steps={['Add Match Play', 'Who’s in', 'Play', 'Results & settle']}
        note="Steps 1 and 2 happen before the round; 3 and 4 on the day. Tap a step to jump to it."
      />

      <H2>Before you start</H2>
      <UL>
        <LI>
          <strong>Match Play needs live scoring.</strong> Every player in it needs his scores entered
          in the app as he plays, on his own phone or by the player keeping his group&rsquo;s card.
        </LI>
        <LI>
          <strong>Match Play isn&rsquo;t in your games list?</strong> Games come with the Plus plan and
          above. On Free or Starter, upgrade under Settings &rarr; Subscription &amp; Billing.
        </LI>
        <LI>
          <strong>Set the groups on the Pairings page.</strong> Matches are played inside each group.
          For four-ball, put the partners first and second, and their opponents third and fourth.
        </LI>
      </UL>

      <H2>Setup, step by step</H2>

      <Step n={1} title="Add Match Play" when="Before the round">
        <Heads>
          <strong>On a net game, every player needs a Handicap Index</strong> (and a preferred tee, so
          his course handicap can be worked out).
        </Heads>
        <UL>
          <LI>
            Click <Btn>🎮 Games</Btn> on the tee time card and tick <strong>Match Play</strong>.
          </LI>
          <LI>
            <strong>Matches:</strong>
            <UL>
              <LI>
                <Btn>Singles — every player vs every player in his group</Btn>: three matches each in a
                foursome, two in a threesome.
              </LI>
              <LI>
                <Btn>2 vs 2 four-ball</Btn>: in each foursome the first two names on the pairing play
                the last two. A group that isn&rsquo;t four players has no match.
              </LI>
            </UL>
          </LI>
          <LI>
            <strong>Strokes:</strong> <Btn>Net — USGA</Btn> (the default: singles off the low man;
            four-ball 90% of each player&rsquo;s course handicap, then off the low man),{' '}
            <Btn>Net — full course handicap</Btn> or <Btn>Gross</Btn>.
          </LI>
          <LI>
            <strong>Wager:</strong> the amount <strong>per match</strong>. The winner collects it; in
            four-ball each player collects it from one player on the other side. A $0 wager makes it
            results only.
          </LI>
          <LI>
            Click <Btn>Save Games</Btn>.
          </LI>
        </UL>
      </Step>

      <Step n={2} title="Choose who's in" when="After you save, until the first score">
        <UL>
          <LI>
            After you save, the game shows a <strong>Player Opt-Ins</strong> list. Click a
            player&rsquo;s <strong>— Out</strong> to make it <strong>✓ In</strong>, or the reverse.
          </LI>
          <LI>
            Players can also tap <Btn>Opt In</Btn> in their own app until the first score of the round
            is entered.
          </LI>
        </UL>
      </Step>

      <H2>On the day</H2>

      <Step n={3} title="Play" when="During the round">
        <UL>
          <LI>Each player enters his scores hole by hole, or one player keeps the card for his group.</LI>
          <LI>
            The matches are in the app: <strong>🏆 Leaderboard</strong> &rarr;{' '}
            <strong>💰 Games</strong>. Each of your matches reads from your side, for example{' '}
            <em>&ldquo;You vs Bob — 2 up thru 12&rdquo;</em>, <em>&ldquo;1 down (dormie) thru 16&rdquo;</em>{' '}
            or <em>&ldquo;won 3 &amp; 2&rdquo;</em>.
          </LI>
          <LI>
            A hole counts once every player in the match has a score on it. Shotgun starts are fine:
            each group&rsquo;s holes count in the order it played them.
          </LI>
        </UL>
      </Step>

      <Step n={4} title="Results and settling up" when="When the matches are decided">
        <UL>
          <LI>
            The round finalizes itself once every card is complete, or click{' '}
            <Btn>🏁 Finalize Round &amp; Save Settlements</Btn> in <Btn>🎮 Games</Btn>.
          </LI>
          <LI>
            Each match won collects the wager; a halved match pays nothing. Every player gets a
            statement showing whom he pays or collects from, for example{' '}
            <em>&ldquo;Match Play · won 3 &amp; 2&rdquo;</em>.
          </LI>
        </UL>
        <H3>Examples</H3>
        <UL>
          <LI>
            <strong>Singles, $5 a match, a foursome:</strong> Al beats Bob 2 &amp; 1, halves with Cal and
            loses to Don 1 up. Al collects $5 from Bob and pays Don $5: even.
          </LI>
          <LI>
            <strong>Four-ball, $10 a match:</strong> Al &amp; Bob beat Cal &amp; Don 3 &amp; 2. Al collects $10
            from Cal and Bob collects $10 from Don.
          </LI>
        </UL>
      </Step>

      <H2>Running match play in an event?</H2>
      <P>
        Set the event up with its format guide (📖 Guides &rarr; Setting up Events / Tournaments) and
        tick <strong>Match Play</strong> at the step where you add the games. The matches are within
        each group of the round; brackets over several rounds aren&rsquo;t built yet.
      </P>

      <FAQ id="match-play-game" faqs={FAQS} />
      <div className="guide-no-print">
        <GuideCTA />
      </div>
    </article>
  );
}
